import { createContext, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import intercomService from "../utils/intercomService";

const IntercomContext = createContext(undefined);

export const useIntercom = () => {
  const context = useContext(IntercomContext);
  if (!context) {
    throw new Error("useIntercom must be used within an IntercomProvider");
  }
  return context;
};

export const IntercomProvider = ({ children }) => {
  const { user } = useAuth();

  // Check if user is authenticated based on user existence
  const isAuthenticated = !!user;

  useEffect(() => {
    // Initialize Intercom when the provider mounts
    intercomService.init();

    // Cleanup on unmount
    return () => {
      intercomService.reset();
    };
  }, []);

  useEffect(() => {
    // Identify user when user data changes
    if (isAuthenticated && user) {
      intercomService.identifyUser(user);
    } else {
      // Reset Intercom when user logs out
      intercomService.reset();
      // Re-initialize for anonymous users
      intercomService.init();
    }
  }, [user, isAuthenticated]);

  const contextValue = {
    show: () => intercomService.show(),
    hide: () => intercomService.hide(),
    showMessage: (message) => intercomService.showMessage(message),
    showNewMessage: (message) => intercomService.showNewMessage(message),
    trackEvent: (eventName, metadata) =>
      intercomService.trackEvent(eventName, metadata),
    identifyUser: (user) => intercomService.identifyUser(user),
    reset: () => intercomService.reset(),
  };

  return (
    <IntercomContext.Provider value={contextValue}>
      {children}
    </IntercomContext.Provider>
  );
};

export default IntercomProvider;
